import { motion } from 'framer-motion'
import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { pageUrl } from '../seo'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

export default function PessoasTuristas() {
    const { t } = useTranslation()
    const vulnerabilityItems = t('turistas.vulnerabilidade.items', { returnObjects: true })
    const crimeTypes = t('turistas.violencia.crimes', { returnObjects: true })

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            {/* SEO por rota — no React 19 estas tags são içadas para o <head> */}
            <title>{t('turistas.metaTitle')}</title>
            <meta name="description" content={t('turistas.metaDescription')} />
            <link rel="canonical" href={pageUrl('pessoas-turistas')} />
            {/* Hero Section */}
            <section
                className="relative overflow-hidden py-16 lg:py-20 bg-cover bg-center"
                style={{ backgroundImage: "url('/assets/images/hero-bg.jpg')" }}
            >
                <div className="absolute inset-0 bg-black/55" />
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            {t('turistas.title')}
                        </h1>
                        <p className="mt-5 text-base lg:text-lg text-slate-200 leading-relaxed">
                            {t('turistas.lead')}
                        </p>
                        <a
                            href="https://www.travelsafe-abroad.com/portugal/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 inline-flex items-center gap-2 text-brand-300 font-bold hover:underline"
                        >
                            {t('turistas.travelSafeLink')} <ExternalLink size={14} aria-hidden="true" />
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* Vulnerabilidade */}
            <section className="section-padding bg-white">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">{t('turistas.vulnerabilidade.title')}</h2>
                        <p className="text-slate-700 leading-relaxed text-lg mb-8">
                            {t('turistas.vulnerabilidade.intro')}
                        </p>
                        <ol className="space-y-3 list-none p-0 m-0">
                            {vulnerabilityItems.map((text, i) => (
                                <motion.li
                                    key={i}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.06 }}
                                    className="flex gap-4 items-start bg-white border border-slate-100 rounded-2xl p-5 shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                                >
                                    <div aria-hidden="true" className="shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 flex items-center justify-center text-white font-bold text-sm shadow-md">
                                        {i + 1}
                                    </div>
                                    <p className="text-slate-700 leading-relaxed text-base pt-1">{text}</p>
                                </motion.li>
                            ))}
                        </ol>
                    </motion.div>
                </div>
            </section>

            {/* Violência contra pessoas turistas */}
            <section className="section-padding bg-slate-50">
                <div className="container-custom">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} transition={{ duration: 0.6 }}>
                        <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-6">{t('turistas.violencia.title')}</h2>
                        <p className="text-slate-700 leading-relaxed text-lg mb-10">
                            {t('turistas.violencia.intro')}
                        </p>
                        <div className="space-y-8">
                            {crimeTypes.map((item, i) => (
                                <motion.div
                                    key={i}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: i * 0.08 }}
                                    className="bg-white rounded-2xl p-7 shadow-xs border border-slate-100"
                                >
                                    <h3 className="text-lg font-bold text-brand-700 mb-3">
                                        <span aria-hidden="true">&gt;</span> {item.title}
                                    </h3>
                                    <p className="text-slate-700 leading-relaxed mb-3">{item.intro}</p>
                                    {item.bullets.length > 0 && (
                                        <ul className="space-y-1 mt-3">
                                            {item.bullets.map((b, j) => (
                                                <li key={j} className="text-slate-700 flex items-center gap-2">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
                                                    {b}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {item.extra && (
                                        <p className="text-slate-700 leading-relaxed mt-3">{item.extra}</p>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>
        </motion.div>
    )
}
